import {
  CarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FireOutlined,
  MoreOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  Modal,
  PageHeader,
  Row,
  Select,
  Space,
  Spin,
  Tag
} from "antd";
import { Component } from "react";
import { connect } from "react-redux";
import socketIOClient from "socket.io-client";
import logger from "../../utils/logger.js";
import { GET, UPDATE } from "../constants/request-type";
import {
  get_all_order,
  get_all_user,
  order,
  update_order,
  user
} from "../constants/routes";
import { service_api, socket_api } from "../constants/url";
import OrderDetails from "../forms/order_details";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import { formatDateTime } from "../functions/dateFunctions";
import { openNotificationWithIcon } from "../functions/notification";
import "../styles/kanban.css";

const { Option } = Select;
const token = sessionStorage.getItem("auth_token");

// Order status configuration
const ORDER_STATUSES = {
  0: { title: "Pending", color: "#FFA500", icon: <ClockCircleOutlined /> },
  1: { title: "Ready", color: "#1890FF", icon: <FireOutlined /> },
  2: { title: "On Way", color: "#722ED1", icon: <CarOutlined /> },
  3: { title: "Delivered", color: "#52C41A", icon: <CheckCircleOutlined /> },
  4: { title: "Canceled", color: "#FF4D4F", icon: <CloseCircleOutlined /> }
};

class OrderKanban extends Component {
  state = {
    load: true,
    orders: [],
    chefs: [],
    selectedChef: "all",
    draggedOrder: null,
    isModalOpen: false,
    selectedOrder: null,
    socket: null,
    autoRefresh: true
  };

  componentDidMount() {
    this.getData();
    this.initSocket();
    // Auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      if (this.state.autoRefresh) {
        this.getData(true);
      }
    }, 30000);
  }

  componentWillUnmount() {
    if (this.state.socket) {
      this.state.socket.disconnect();
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  initSocket = () => {
    const socket = socketIOClient(socket_api);

    socket.on("connect", () => {
      logger.log("Socket connected for kanban board");
      socket.emit("join", "Admin");
    });

    socket.on("update-trip", (updatedOrder) => {
      this.handleOrderUpdate(updatedOrder);
    });

    socket.on("new-trip", (newOrder) => {
      this.getData(true);
    });

    this.setState({ socket });
  };

  handleOrderUpdate = (updatedOrder) => {
    const { orders } = this.state;
    const orderIndex = orders.findIndex((o) => o._id === updatedOrder._id);

    if (orderIndex !== -1) {
      const newOrders = [...orders];
      newOrders[orderIndex] = { ...newOrders[orderIndex], ...updatedOrder };
      this.setState({ orders: newOrders });
    } else {
      // New order, refresh data
      this.getData(true);
    }
  };

  getData = async (silent = false) => {
    if (!silent) {
      this.setState({ load: true });
    }

    try {
      // Fetch orders
      const orderRequest = {
        type: GET,
        url: service_api,
        route: order + "/" + get_all_order,
        data: null,
        token: token
      };

      const orderResponse = await MakeRequestAsync(orderRequest);

      if (orderResponse.status && orderResponse.data.order) {
        const decryptedOrders = JSON.parse(
          decryptSingleData(orderResponse.data.order)
        );

        // Filter only paid orders and exclude refunded status
        const filteredOrders = decryptedOrders.filter(
          (o) => o.paid === true && o.status !== 5 && o.status !== 6
        );

        // Fetch chefs
        const chefRequest = {
          type: GET,
          url: service_api,
          route: user + "/" + get_all_user,
          data: null,
          token: token
        };

        const chefResponse = await MakeRequestAsync(chefRequest);

        if (chefResponse.status && chefResponse.data.user) {
          const decryptedChefs = JSON.parse(
            decryptSingleData(chefResponse.data.user)
          );

          const chefList = decryptedChefs.filter((u) => u.chef === true);

          this.setState({
            orders: filteredOrders,
            chefs: chefList,
            load: false
          });
        }
      }
    } catch (error) {
      logger.error("Error fetching data:", error);
      openNotificationWithIcon("error", "Error", "Failed to fetch orders");
      this.setState({ load: false });
    }
  };

  handleDragStart = (e, order) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("orderId", order._id);
    this.setState({ draggedOrder: order });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData("orderId");
    const { draggedOrder } = this.state;

    if (!draggedOrder || draggedOrder._id !== orderId) return;

    // Prevent invalid status transitions
    if (!this.isValidStatusTransition(draggedOrder.status, newStatus)) {
      openNotificationWithIcon(
        "warning",
        "Invalid Action",
        `Cannot move order from ${ORDER_STATUSES[draggedOrder.status].title} to ${ORDER_STATUSES[newStatus].title}`
      );
      return;
    }

    await this.updateOrderStatus(orderId, newStatus);
    this.setState({ draggedOrder: null });
  };

  isValidStatusTransition = (currentStatus, newStatus) => {
    // Define valid transitions
    const validTransitions = {
      0: [1, 4], // Pending can go to Ready or Canceled
      1: [2, 4], // Ready can go to On Way or Canceled
      2: [3, 4], // On Way can go to Delivered or Canceled
      3: [], // Delivered is final
      4: [] // Canceled is final
    };

    return validTransitions[currentStatus]?.includes(newStatus) || false;
  };

  updateOrderStatus = async (orderId, newStatus) => {
    try {
      const request = {
        type: UPDATE,
        url: service_api,
        route: order + "/" + update_order + "/" + orderId,
        data: { status: newStatus },
        token: token
      };

      const response = await MakeRequestAsync(request);

      if (response.status) {
        openNotificationWithIcon(
          "success",
          "Success",
          `Order status updated to ${ORDER_STATUSES[newStatus].title}`
        );

        // Update local state
        const { orders } = this.state;
        const updatedOrders = orders.map((o) =>
          o._id === orderId ? { ...o, status: newStatus } : o
        );
        this.setState({ orders: updatedOrders });
      }
    } catch (error) {
      logger.error("Error updating order:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        "Failed to update order status"
      );
    }
  };

  showOrderDetails = (order) => {
    this.setState({
      selectedOrder: order,
      isModalOpen: true
    });
  };

  handleModalClose = () => {
    this.setState({
      isModalOpen: false,
      selectedOrder: null
    });
  };

  getOrdersByStatus = (status) => {
    const { orders, selectedChef } = this.state;

    let filtered = orders.filter((o) => o.status === status);

    if (selectedChef !== "all") {
      filtered = filtered.filter((o) => o.chef_id?._id === selectedChef);
    }

    return filtered;
  };

  handleChefFilter = (value) => {
    this.setState({ selectedChef: value });
  };

  renderOrderCard = (order) => {
    const statusConfig = ORDER_STATUSES[order.status];

    return (
      <Card
        key={order._id}
        className="kanban-card"
        draggable
        onDragStart={(e) => this.handleDragStart(e, order)}
        hoverable
        onClick={() => this.showOrderDetails(order)}
        style={{
          marginBottom: 12,
          borderLeft: `4px solid ${statusConfig.color}`,
          cursor: "grab"
        }}
        bodyStyle={{ padding: 12 }}
      >
        <div className="kanban-card-header">
          <Space>
            <Avatar
              size="small"
              style={{ backgroundColor: statusConfig.color }}
              icon={statusConfig.icon}
            />
            <strong>#{order._id.slice(-6)}</strong>
          </Space>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="1"
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    this.showOrderDetails(order);
                  }}
                >
                  View Details
                </Menu.Item>
                {this.isValidStatusTransition(order.status, 4) && (
                  <Menu.Item
                    key="2"
                    danger
                    onClick={(e) => {
                      e.domEvent.stopPropagation();
                      this.updateOrderStatus(order._id, 4);
                    }}
                  >
                    Cancel Order
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>

        <div style={{ marginTop: 8 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div>
              <UserOutlined style={{ marginRight: 4, color: "#8c8c8c" }} />
              <span className="kanban-card-text">
                {order.chef_id?.firstname} {order.chef_id?.lastname}
              </span>
            </div>

            <div>
              <EnvironmentOutlined
                style={{ marginRight: 4, color: "#8c8c8c" }}
              />
              <span className="kanban-card-text">
                {order.delivery_details?.street_name || "N/A"}
              </span>
            </div>

            <div>
              <DollarOutlined style={{ marginRight: 4, color: "#52c41a" }} />
              <strong>
                ${order.billing_details?.amount?.toFixed(2) || "0.00"}
              </strong>
            </div>

            <div className="kanban-card-footer">
              <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                {formatDateTime(order.createdAt)}
              </span>
              <Tag color={statusConfig.color} style={{ margin: 0 }}>
                {statusConfig.title}
              </Tag>
            </div>
          </Space>
        </div>
      </Card>
    );
  };

  renderKanbanColumn = (status) => {
    const statusConfig = ORDER_STATUSES[status];
    const orders = this.getOrdersByStatus(status);

    return (
      <Col
        xs={24}
        sm={12}
        md={8}
        lg={status === 0 || status === 1 ? 6 : 4}
        key={status}
      >
        <Card
          className="kanban-column"
          onDragOver={this.handleDragOver}
          onDrop={(e) => this.handleDrop(e, status)}
          style={{
            minHeight: 600,
            backgroundColor: "#f5f5f5"
          }}
        >
          <div
            className="kanban-column-header"
            style={{ borderBottom: `3px solid ${statusConfig.color}` }}
          >
            <Space>
              {statusConfig.icon}
              <strong>{statusConfig.title}</strong>
              <Badge
                count={orders.length}
                style={{ backgroundColor: statusConfig.color }}
              />
            </Space>
          </div>

          <div className="kanban-column-body" style={{ marginTop: 16 }}>
            {orders.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: 20, color: "#8c8c8c" }}
              >
                No orders
              </div>
            ) : (
              orders.map((order) => this.renderOrderCard(order))
            )}
          </div>
        </Card>
      </Col>
    );
  };

  render() {
    const { load, chefs, selectedChef, isModalOpen, selectedOrder } =
      this.state;

    return (
      <div className="order-kanban-container">
        <PageHeader
          title="Order Fulfillment Board"
          subTitle="Drag and drop orders to update their status"
          extra={[
            <Select
              key="chef-filter"
              style={{ width: 200 }}
              placeholder="Filter by Chef"
              value={selectedChef}
              onChange={this.handleChefFilter}
            >
              <Option value="all">All Chefs</Option>
              {chefs.map((chef) => (
                <Option key={chef._id} value={chef._id}>
                  {chef.firstname} {chef.lastname}
                </Option>
              ))}
            </Select>,
            <Button key="refresh" onClick={() => this.getData()}>
              Refresh
            </Button>
          ]}
        />

        <Spin spinning={load} tip="Loading orders...">
          <div style={{ padding: "0 24px" }}>
            <Row gutter={[16, 16]}>
              {[0, 1, 2, 3, 4].map((status) => this.renderKanbanColumn(status))}
            </Row>
          </div>
        </Spin>

        <Modal
          title="Order Details"
          open={isModalOpen}
          onCancel={this.handleModalClose}
          footer={null}
          width={800}
        >
          {selectedOrder && <OrderDetails order={selectedOrder} />}
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  admin: state.admin
});

export default connect(mapStateToProps)(OrderKanban);
